import { type InputHTMLAttributes, type ReactNode, useId } from 'react'
import { cn } from '@/shared/lib'

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  // Задан — компонент сам рендерит <label htmlFor> (клик по тексту переключает);
  // иначе отдаёт голую коробку, и потребитель вешает aria-label сам.
  label?: ReactNode
  labelClassName?: string
}

// Нативный input под appearance-none + SVG-галочка поверх: вид свой, а
// checked/disabled/aria/участие в форме остаются нативными.
export function Checkbox({ className, label, labelClassName, id, ...rest }: CheckboxProps) {
  const autoId = useId()
  const inputId = id ?? autoId

  const box = (
    <span className="relative inline-grid size-4 shrink-0 place-items-center">
      <input
        id={inputId}
        type="checkbox"
        className={cn(
          'peer col-start-1 row-start-1 size-4 cursor-pointer appearance-none rounded-[5px]',
          'border border-border-strong bg-bg-sunken transition-colors',
          'hover:border-border-accent',
          'checked:border-accent checked:bg-accent',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      <svg
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="pointer-events-none col-start-1 row-start-1 size-3 scale-50 text-text-inverse opacity-0 transition peer-checked:scale-100 peer-checked:opacity-100"
      >
        <path
          d="M3.5 8.4l2.9 2.9L12.6 5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )

  if (label == null) return box

  return (
    <label
      htmlFor={inputId}
      // Коробка сама по себе 16px — пальцем не попасть. Мишень даёт строка целиком:
      // -my/py разгоняют её до ~36px (на таче ~44px), не раздвигая ряд визуально.
      className={cn(
        '-my-1.5 inline-flex cursor-pointer items-center gap-2 py-1.5 touch:-my-3 touch:py-3',
        labelClassName,
      )}
    >
      {box}
      {label}
    </label>
  )
}
